import * as React from 'react';

import config from 'js/app/config';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { numberOfProducts } from 'bundles/coursera-plus/utils/courseraPlusUtils';

import _t from 'i18n!nls/coursera-plus-landing-page';

export type ReasonType = {
  intro?: string;
  reason: string | JSX.Element;
};

export type BlockContent = {
  title: string;
  subtitle: string;
  actionLabel: string;
  actionComment?: string | JSX.Element;
  reasonIntro?: string;
  reasons: ReasonType[];
};

export const enrollmentButtonLabel = (trialAvailable: boolean): string => {
  return trialAvailable ? _t('Start 7-day free trial') : _t('Renew Subscription');
};

export const getMainTitle = () => _t('Plans for you or your team');

export const getCoursePriceBlock = (): BlockContent => {
  return {
    title: _t('Single learning program'),
    subtitle: _t('Learn a single topic or skill and earn a credential'),
    actionLabel: _t('Visit an individual course or Specialization page to purchase.'),
    reasons: [
      { reason: _t('Access all courses within the learning program') },
      {
        reason: _t('Earn a certificate upon completion after your trial ends'),
      },
    ],
  };
};

export const getMonthlyPriceBlock = ({
  trialAvailable,
  ownsCourseraLite,
}: {
  trialAvailable?: boolean;
  ownsCourseraLite?: boolean;
}): BlockContent => {
  return {
    title: _t('Coursera Plus Monthly'),
    subtitle: _t('Complete multiple courses and earn credentials in the short term'),
    actionLabel: ownsCourseraLite ? _t('Upgrade to Coursera Plus') : enrollmentButtonLabel(trialAvailable !== false),
    actionComment: _t('Cancel anytime'),
    reasons: [
      {
        reason: (
          <FormattedMessage
            message={_t(
              'Access {numOfProducts} courses and Specializations from 170+ leading companies and universities'
            )}
            numOfProducts={numberOfProducts}
          />
        ),
      },
      {
        reason: _t('Earn unlimited certificates after your trial ends'),
      },
      {
        reason: _t(
          'Learn job-relevant skills and tools with 1,000+ applied projects and hands-on labs from industry experts'
        ),
      },
      {
        reason: _t(
          'Choose from more than 15 Professional Certificate programs from industry leaders like Google, Facebook, and more'
        ),
      },
    ],
  };
};

export const getYearlyPriceBlock = ({
  savingsAmount,
  isAnnualWithFreeTrialEpicTestVariant,
  userHasCourseraPlusFreeTrial,
  ownsCourseraLite,
}: {
  savingsAmount?: JSX.Element;
  isAnnualWithFreeTrialEpicTestVariant: boolean;
  userHasCourseraPlusFreeTrial: boolean;
  ownsCourseraLite: boolean;
}) => {
  const comment = isAnnualWithFreeTrialEpicTestVariant ? _t('Cancel anytime') : _t('14-day money-back guarantee');
  let actionLabel;
  if (isAnnualWithFreeTrialEpicTestVariant && userHasCourseraPlusFreeTrial) {
    actionLabel = _t('Start 7-day free trial');
  } else if (!userHasCourseraPlusFreeTrial) {
    actionLabel = _t('Renew Subscription');
  } else {
    actionLabel = _t('Try Coursera Plus Annual');
  }

  return {
    title: _t('Coursera Plus Annual'),
    subtitle: _t('Combine flexibility and savings with long-term learning goals'),
    actionLabel: ownsCourseraLite ? _t('Upgrade to Coursera Plus') : actionLabel,
    actionComment: comment,
    reasonIntro: _t('Everything included in the monthly plan, plus:'),
    reasons: [
      {
        reason: (
          <FormattedMessage
            message={
              isAnnualWithFreeTrialEpicTestVariant
                ? _t('Save {savingsAmount} when you pay for a year')
                : _t('Save {savingsAmount} when you pay up front for the year')
            }
            savingsAmount={savingsAmount}
          />
        ),
      },
      { reason: _t('Enjoy maximum flexibility to achieve work/life balance and learn at your own pace') },
    ],
  };
};

export const getWesHero = () => {
  return {
    title: _t('Accelerate team performance'),
    description: _t(
      'Help employees master new skills and reach their goals with access to world-class learning programs from Coursera for Teams.'
    ),
  };
};

export const getWesPriceBlock = () => {
  return {
    title: _t('Coursera for Teams'),
    subtitle: _t('For 5-125 Users'),
    priceLabel: _t('Per user for 12 months'),
    actionLabel: _t('Get Started'),
    actionComment: _t('14-day money-back guarantee'),
    actionLink: `${config.url.base}purchase/plan?utm_medium=coursera&utm_source=plans-module&utm_campaign=website&utm_content=coursera-plus`,
    reasons: [
      {
        reason: _t('Upskill 5 to 125 employees'),
      },
      { reason: _t('Unlimited access to 8,500 learning opportunities') },
      { reason: _t('Program setup and launch tools') },
      { reason: _t('Analytics and benchmarking dashboard') },
    ],
  };
};
